/**
 * Single source of truth for the Product Module taxonomy.
 *
 * Categories drive the top-level tab strip in the Product module; each
 * category exposes a set of subtypes that map 1:1 onto the backend
 * `product_type` slug list defined in `backend/stock/models.py`.
 *
 * Keep this file in sync with:
 *   - backend/stock/models.py (CATEGORY_CHOICES, PRODUCT_SUBTYPE_CHOICES,
 *     CATEGORY_TO_SUBTYPES)
 *   - the `categories` action on `StockItemViewSet`
 */

import {
    faCube, faIndustry, faHammer, faBolt, faGasPump, faOilCan, faFire,
    faWind, faMagnet, faGem, faMountain, faBuilding, faTree, faDotCircle,
    faEllipsisH, faOilCan as faLubeCan,
} from '@fortawesome/free-solid-svg-icons';

export const SUBTYPE_LABEL_MAX_LENGTH = 80;

// Branded categories are identified by brand + product name rather than by
// subtype/origin, and render no subtype selector. Keep in sync with
// LUBRICATION_CATEGORY in backend/stock/models.py.
export const LUBRICATION_CATEGORY = 'lubrication';

export const isBrandedCategory = (categoryValue) => categoryValue === LUBRICATION_CATEGORY;

/**
 * Which product catalogue category a bunkers deal line belongs in.
 *
 * Bunker product cards carry a Bunkers/Lubes toggle (``product_kind``). A Lubes
 * line is a Lubrication product -- named brand + product name -- and a Bunkers
 * line is an Oil / Gas one, named by subtype + origin. Getting this wrong does
 * not just mis-file the row, it applies the wrong naming rule to it.
 *
 * ONE owner, because the "+ Create product" modal is shared across every card
 * and every deal surface; the rule was previously a hard-coded 'oil_gas'
 * literal at the render site, which filed every lube a trader created as a fuel.
 */
export const categoryForProductKind = (productKind) => (
    productKind === 'lubes' ? LUBRICATION_CATEGORY : 'oil_gas'
);

export const CATEGORIES = [
    {
        value: 'coal',
        label: 'Coal',
        icon: faBolt,
        gradient: 'linear-gradient(135deg, #000000 0%, #5C258D 100%)',
        subtypes: [
            { value: 'steam_coal', label: 'Steam Coal', icon: faBolt },
            { value: 'anthracite', label: 'Anthracite', icon: faCube },
            { value: 'low_vol_semi_anthracite', label: 'Low Vol / Semi-Anthracite', icon: faCube },
            { value: 'coke', label: 'Coke', icon: faIndustry },
            { value: 'pci', label: 'PCI', icon: faHammer },
        ],
    },
    {
        value: 'oil_gas',
        label: 'Oil / Gas',
        icon: faGasPump,
        gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
        subtypes: [
            { value: 'petcoke', label: 'Petcoke', icon: faOilCan },
            { value: 'diesel', label: 'Diesel', icon: faGasPump },
            { value: 'gasoline', label: 'Gasoline', icon: faGasPump },
            { value: 'mixed_fuels', label: 'Mixed Fuels', icon: faFire },
            { value: 'natural_gas', label: 'Natural Gas', icon: faWind },
            { value: 'lng', label: 'LNG', icon: faWind },
            { value: 'jet_fuel', label: 'Jet Fuel', icon: faFire },
        ],
    },
    {
        // HMS asked for Lubrication with no sub-categories. The single subtype
        // below exists so the backend's product_type stays a real validated value;
        // ``hideSubtypes`` is what stops the UI ever asking for it.
        value: 'lubrication',
        label: 'Lubrication',
        icon: faLubeCan,
        gradient: 'linear-gradient(135deg, #0F2027 0%, #2C5364 100%)',
        hideSubtypes: true,
        branded: true,
        subtypes: [
            { value: 'lubrication', label: 'Lubrication', icon: faLubeCan },
        ],
    },
    {
        value: 'minerals',
        label: 'Minerals',
        icon: faGem,
        gradient: 'linear-gradient(135deg, #C9D6FF 0%, #4A6FA5 100%)',
        subtypes: [
            { value: 'iron_ore', label: 'Iron Ore', icon: faMagnet },
            { value: 'chrome_ore', label: 'Chrome Ore', icon: faGem },
            { value: 'lithium_ore', label: 'Lithium Ore', icon: faGem },
            { value: 'manganese_ore', label: 'Manganese Ore', icon: faGem },
            { value: 'beryllium_ore', label: 'Beryllium Ore', icon: faGem },
            { value: 'copper_ore', label: 'Copper Ore', icon: faGem },
        ],
    },
    {
        value: 'metals',
        label: 'Metals',
        icon: faMountain,
        gradient: 'linear-gradient(135deg, #757F9A 0%, #D7DDE8 100%)',
        subtypes: [
            { value: 'copper', label: 'Copper', icon: faMountain },
            { value: 'steel_products', label: 'Steel Products', icon: faIndustry },
            { value: 'various', label: 'Various', icon: faEllipsisH },
        ],
    },
    {
        value: 'cementitious',
        label: 'Cementitious',
        icon: faBuilding,
        gradient: 'linear-gradient(135deg, #BDC3C7 0%, #2C3E50 100%)',
        subtypes: [
            { value: 'cement', label: 'Cement', icon: faBuilding },
            { value: 'clinker', label: 'Clinker', icon: faBuilding },
            { value: 'fly_ash', label: 'Fly Ash', icon: faBuilding },
            { value: 'aggregates', label: 'Aggregates', icon: faMountain },
        ],
    },
    {
        value: 'biomass',
        label: 'Biomass',
        icon: faTree,
        gradient: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)',
        subtypes: [
            { value: 'wood_pellets', label: 'Wood Pellets', icon: faDotCircle },
            { value: 'wood_chips', label: 'Wood Chips', icon: faTree },
            { value: 'pks', label: 'PKS', icon: faDotCircle },
            { value: 'etc', label: 'Etc', icon: faEllipsisH },
        ],
    },
    {
        value: 'other',
        label: 'Other',
        icon: faEllipsisH,
        gradient: 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
        subtypes: [
            { value: 'other', label: 'Other', icon: faEllipsisH },
        ],
    },
];

export const CATEGORY_VALUES = CATEGORIES.map((c) => c.value);

export const SUBTYPE_LOOKUP = (() => {
    const map = {};
    CATEGORIES.forEach((cat) => {
        cat.subtypes.forEach((sub) => {
            map[sub.value] = { ...sub, category: cat.value };
        });
    });
    return map;
})();

export const CATEGORY_LOOKUP = (() => {
    const map = {};
    CATEGORIES.forEach((cat) => {
        map[cat.value] = cat;
    });
    return map;
})();

export function getCategoryForSubtype(subtypeValue) {
    return SUBTYPE_LOOKUP[subtypeValue]?.category || null;
}

export function getSubtypeLabel(subtypeValue) {
    return SUBTYPE_LOOKUP[subtypeValue]?.label || '';
}

export function getCategoryLabel(categoryValue) {
    return CATEGORY_LOOKUP[categoryValue]?.label || '';
}

export function getSubtypesForCategory(categoryValue) {
    return CATEGORY_LOOKUP[categoryValue]?.subtypes || [];
}

const HTML_TAG_RE = /<[^>]*>/g;
const WHITESPACE_RE = /\s+/g;
const LETTER_RE = /[A-Za-z]/;

/**
 * Sanitise/normalise a free-text Other-category subtype label.
 * Mirrors backend `normalize_subtype_label`.
 */
export function normalizeSubtypeLabel(raw) {
    if (raw === null || raw === undefined) return '';
    let text = String(raw).replace(HTML_TAG_RE, '');
    text = text
        .split('')
        .filter((ch) => ch === '\n' || ch === '\t' || ch.charCodeAt(0) >= 32)
        .join('');
    text = text.replace(WHITESPACE_RE, ' ').trim();
    if (!text) return '';
    text = text.slice(0, SUBTYPE_LABEL_MAX_LENGTH);
    return text
        .split(' ')
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
        .join(' ');
}

/**
 * Validate a free-text subtype label. Returns null if valid, otherwise an
 * error message suitable for display under the input.
 */
export function validateSubtypeLabel(raw) {
    const cleaned = normalizeSubtypeLabel(raw);
    if (!cleaned) return 'A descriptive label is required.';
    if (!LETTER_RE.test(cleaned)) return 'Label must contain at least one letter.';
    if (cleaned.length > SUBTYPE_LABEL_MAX_LENGTH) {
        return `Label is too long (max ${SUBTYPE_LABEL_MAX_LENGTH} characters).`;
    }
    return null;
}

export default {
    CATEGORIES,
    CATEGORY_LOOKUP,
    SUBTYPE_LOOKUP,
    CATEGORY_VALUES,
    SUBTYPE_LABEL_MAX_LENGTH,
    getCategoryForSubtype,
    getSubtypeLabel,
    getCategoryLabel,
    getSubtypesForCategory,
    normalizeSubtypeLabel,
    validateSubtypeLabel,
};
